import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowLeft, ArrowRight, Gavel, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  AppealRole,
  AppealSession,
  createRoom,
  isCodeShaped,
  joinRoom,
  readSession,
  resolveCode,
  saveSession,
} from '../../../../services/appealsService';
import { useAppealRoom } from '../../../../hooks/useAppealRoom';
import AppealOwnerView from './AppealOwnerView';
import AppealTeamView from './AppealTeamView';
import AppealJuryView from './AppealJuryView';
import { ErrorText, cardCls, fieldCls, labelCls, primaryBtnCls, secondaryBtnCls } from './shared';

const NAME_PROMPT: Record<Exclude<AppealRole, 'owner'>, { label: string; placeholder: string }> = {
  team: { label: 'Komandanızın adı', placeholder: 'Məs: Nərgizlər' },
  jury: { label: 'Adınız (jüri)', placeholder: 'Məs: Əli Məmmədov' },
};

// ─── Lobby ────────────────────────────────────────────────────────────────────

const Lobby: React.FC<{ onEnter: (session: AppealSession) => void }> = ({ onEnter }) => {
  const [code, setCode] = useState('');
  // Set once the code is recognised as a team or jury code and a name is needed
  const [pending, setPending] = useState<{ role: 'team' | 'jury'; roomName: string; questionNumber: number | null } | null>(null);
  const [name, setName] = useState('');
  const nameRef = useRef<HTMLInputElement | null>(null);
  const [joinError, setJoinError] = useState('');
  const [joining, setJoining] = useState(false);

  const [roomName, setRoomName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Move straight to the name field once the code is recognised
  useEffect(() => {
    if (pending) nameRef.current?.focus();
  }, [pending]);

  const handleCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setJoinError('');
    setJoining(true);
    try {
      if (pending) {
        onEnter(await joinRoom(code, name));
        return;
      }
      const resolved = await resolveCode(code);
      if (resolved.role === 'owner') {
        onEnter(await joinRoom(code, ''));
      } else {
        setPending({ role: resolved.role, roomName: resolved.roomName, questionNumber: resolved.questionNumber });
      }
    } catch (err) {
      setJoinError(err instanceof Error ? err.message : 'Qoşulmaq alınmadı.');
    } finally {
      setJoining(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!roomName.trim()) return setCreateError('Turnirin adını yazın.');
    setCreateError('');
    setCreating(true);
    try {
      onEnter(await createRoom(roomName));
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Otaq yaradılmadı.');
      setCreating(false);
    }
  };

  const codeLooksValid = isCodeShaped(code);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center rounded-2xl bg-rose-100/60 dark:bg-rose-900/30 p-3 text-rose-600 dark:text-rose-400 mb-4">
          <Gavel className="w-8 h-8" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-black text-gray-900 dark:text-gray-50 tracking-tight">Apelyasiya meneceri</h1>
        <p className="mt-3 text-base sm:text-lg text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
          Kodunuzla otağa daxil olun və ya turnir üçün yeni apelyasiya otağı yaradın.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className={cn(cardCls, 'rounded-3xl p-6 sm:p-8')}>
          <h2 className="text-left text-2xl font-bold text-gray-900 dark:text-gray-50 mb-2">Otağa daxil ol</h2>
          <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
            Sualın komanda kodunu, jüri və ya təşkilatçı kodunu daxil edin.
          </p>
          <form onSubmit={handleCode} className="space-y-4">
            <div>
              <label htmlFor="appeal-code" className={labelCls}>Kod</label>
              <input
                id="appeal-code"
                value={code}
                onChange={e => {
                  setCode(e.target.value);
                  setPending(null);
                  setJoinError('');
                }}
                autoComplete="off"
                autoCapitalize="characters"
                maxLength={10}
                placeholder="ABC234"
                className={cn(fieldCls, 'py-3 font-mono text-lg tracking-[0.2em] uppercase')}
              />
            </div>

            {pending && (
              <div className="space-y-4">
                <p className="rounded-xl bg-gray-50 dark:bg-gray-950 px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                  <span className="font-semibold text-gray-900 dark:text-gray-100">{pending.roomName}</span>
                  {' — '}
                  {pending.role === 'team'
                    ? `Sual ${pending.questionNumber ?? ''} üzrə apelyasiya verirsiniz.`
                    : 'jüri kimi daxil olursunuz.'}
                </p>
                <div>
                  <label htmlFor="appeal-name" className={labelCls}>{NAME_PROMPT[pending.role].label}</label>
                  <input
                    id="appeal-name"
                    ref={nameRef}
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder={NAME_PROMPT[pending.role].placeholder}
                    maxLength={80}
                    className={cn(fieldCls, 'py-3')}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Yenidən daxil olanda eyni adı yazın — əvvəlki işiniz qorunur.
                  </p>
                </div>
              </div>
            )}

            {joinError && <ErrorText>{joinError}</ErrorText>}

            <button
              type="submit"
              disabled={joining || !codeLooksValid || (pending !== null && !name.trim())}
              className={cn(primaryBtnCls, 'w-full py-3.5')}
            >
              {joining && <Loader2 className="w-4 h-4 animate-spin" />}
              {pending ? 'Daxil ol' : 'Davam et'}
              {!joining && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>
        </div>

        <div className="rounded-3xl border border-rose-100 dark:border-rose-900 bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-950 dark:to-pink-950 p-6 sm:p-8 shadow-sm">
          <h2 className="text-left text-2xl font-bold text-gray-900 dark:text-gray-50 mb-2">Təşkilatçı</h2>
          <p className="mb-6 text-sm text-gray-600 dark:text-gray-300">
            Otaq yaradın: jürilər üçün bir kod, hər sual üçün komandalara ayrıca kod, sizin üçün isə şəxsi kod veriləcək.
          </p>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label htmlFor="appeal-room" className={labelCls}>Turnirin adı</label>
              <input
                id="appeal-room"
                value={roomName}
                onChange={e => setRoomName(e.target.value)}
                placeholder="Məs: Gənclik kuboku — 2-ci tur"
                maxLength={120}
                className={cn(fieldCls, 'py-3 bg-white')}
              />
            </div>
            {createError && <ErrorText>{createError}</ErrorText>}
            <button type="submit" disabled={creating} className={cn(secondaryBtnCls, 'w-full py-3.5 font-bold')}>
              {creating && <Loader2 className="w-4 h-4 animate-spin" />}
              {creating ? 'Yaradılır...' : 'Yeni otaq yarat'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// ─── Manager ──────────────────────────────────────────────────────────────────

const AppealManager: React.FC = () => {
  const [session, setSession] = useState<AppealSession | null>(readSession);
  const { room, error, expired, loading } = useAppealRoom(session);

  const enter = (next: AppealSession) => {
    saveSession(next);
    setSession(next);
  };

  const leave = () => {
    saveSession(null);
    setSession(null);
  };

  let content: React.ReactNode;
  if (!session) {
    content = <Lobby onEnter={enter} />;
  } else if (loading && !room) {
    content = (
      <div className="flex justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  } else if (!room || expired) {
    content = (
      <div className={cn(cardCls, 'px-6 py-12 text-center space-y-4')}>
        <p className="text-gray-600 dark:text-gray-300">{error || 'Otaq tapılmadı.'}</p>
        <button type="button" onClick={leave} className={secondaryBtnCls}>Geri qayıt</button>
      </div>
    );
  } else if (room.role === 'owner') {
    content = <AppealOwnerView room={room} session={session} onLeave={leave} />;
  } else if (room.role === 'team') {
    content = <AppealTeamView room={room} session={session} onLeave={leave} />;
  } else {
    content = <AppealJuryView room={room} session={session} onLeave={leave} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16">
      <Helmet>
        <title>{room ? `${room.name} | Apelyasiya` : 'Apelyasiya meneceri'} | 3sual.az</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        {!session && (
          <Link
            to="/tools"
            className="inline-flex items-center gap-1 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ArrowLeft className="w-4 h-4" />
            Alətlər
          </Link>
        )}
        {content}
      </div>
    </div>
  );
};

export default AppealManager;
